// src/features/dashboard/components/ManagerDashboard/IdleTimeReport/IdleBreakdownChart.tsx

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import type {
  IdleBreakdownResponse,
} from '../../../types/Manager/idleTime.types';

import {
  CHART_COLORS_500,
} from '../../../constants/chartColors';

// ============================================================================
// PROPS
// ============================================================================

type Props = {
  data: IdleBreakdownResponse[];
};

// ============================================================================
// TYPES
// ============================================================================

type ChartDataItem =
  IdleBreakdownResponse & {
    chartColor: string;
    originalIndex: number;
    isOthers?: boolean;
  };

// ============================================================================
// CONSTANTS
// ============================================================================

const TOP_SUBCATEGORY_COUNT = 10;

// ============================================================================
// COLOR HELPER
// ============================================================================

const getDistributedChartColor = (
  index: number,
  total: number,
): string => {
  if (CHART_COLORS_500.length === 0) {
    return '#2563EB';
  }

  if (total <= 1) {
    return CHART_COLORS_500[0].code;
  }

  const paletteLength =
    CHART_COLORS_500.length;

  const paletteIndex = Math.floor(
    (index * paletteLength) / total,
  );

  const safeIndex = Math.min(
    paletteIndex,
    paletteLength - 1,
  );

  return CHART_COLORS_500[safeIndex].code;
};

// ============================================================================
// PIE LABEL
// ============================================================================

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: any) => {
  if (
    typeof percent !== 'number' ||
    percent <= 0
  ) {
    return null;
  }

  // Hide very small labels
  if (percent < 0.03) {
    return null;
  }

  const RADIAN = Math.PI / 180;

  const radius =
    innerRadius +
    (outerRadius - innerRadius) * 0.52;

  const x =
    cx +
    radius *
      Math.cos(-midAngle * RADIAN);

  const y =
    cy +
    radius *
      Math.sin(-midAngle * RADIAN);

  const displayPercentage =
    Math.round(percent * 100);

  return (
    <text
      x={x}
      y={y}
      fill="#FFFFFF"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={700}
      pointerEvents="none"
    >
      {displayPercentage}%
    </text>
  );
};

// ============================================================================
// TOOLTIP
// ============================================================================

const CustomTooltip = ({
  active,
  payload,
}: any) => {
  if (
    !active ||
    !payload ||
    !payload.length
  ) {
    return null;
  }

  const item =
    payload[0]?.payload;

  if (!item) {
    return null;
  }

  const percentage = Number(
    item.percentage ?? 0,
  );

  const name =
    String(
      item.subCategoryName ?? '',
    ).trim() || 'Unknown';

  return (
    <div
      className="
        min-w-[125px]
        max-w-[220px]
        rounded-xl
        border
        border-slate-200
        bg-white
        px-3
        py-2.5
        shadow-lg
      "
    >
      {/* SUBCATEGORY NAME */}

      <p
        className="
          break-words
          text-[12px]
          font-semibold
          leading-5
          text-[#003E7E]
        "
      >
        {name}
      </p>

      {/* PERCENTAGE */}

      <p
        className="
          mt-2
          border-t
          border-slate-100
          pt-2
          text-[11px]
          font-medium
          text-slate-500
        "
      >
        {percentage.toFixed(1)}%
      </p>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const IdleBreakdownChart = ({
  data,
}: Props) => {
  // ========================================================================
  // TOP 10 + OTHERS DATA
  // ========================================================================

  const chartData =
    useMemo<ChartDataItem[]>(() => {
      if (
        !Array.isArray(data) ||
        data.length === 0
      ) {
        return [];
      }

      // ----------------------------------------------------------------------
      // NORMALIZE DATA
      // ----------------------------------------------------------------------

      const normalizedData =
        data
          .map(
            (
              item,
              index,
            ) => ({
              ...item,

              percentage: Number(
                item.percentage ?? 0,
              ),

              originalIndex:
                index,
            }),
          )
          .filter(
            (item) =>
              Number.isFinite(
                item.percentage,
              ) &&
              item.percentage >= 0,
          );

      // ----------------------------------------------------------------------
      // SORT HIGHEST TO LOWEST
      // ----------------------------------------------------------------------

      const sortedData =
        [...normalizedData].sort(
          (a, b) =>
            b.percentage -
            a.percentage,
        );

      // ----------------------------------------------------------------------
      // TOP 10
      // ----------------------------------------------------------------------

      const topTen =
        sortedData.slice(
          0,
          TOP_SUBCATEGORY_COUNT,
        );

      // ----------------------------------------------------------------------
      // REMAINING DATA
      // ----------------------------------------------------------------------

      const remaining =
        sortedData.slice(
          TOP_SUBCATEGORY_COUNT,
        );

      // ----------------------------------------------------------------------
      // CALCULATE OTHERS
      // ----------------------------------------------------------------------

      const othersPercentage =
        remaining.reduce(
          (
            total,
            item,
          ) =>
            total +
            Number(
              item.percentage ?? 0,
            ),
          0,
        );

      // ----------------------------------------------------------------------
      // TOTAL PIE ITEMS
      //
      // TOP 10 + OTHERS
      // ----------------------------------------------------------------------

      const totalPieItems =
        topTen.length +
        (remaining.length > 0
          ? 1
          : 0);

      // ----------------------------------------------------------------------
      // CREATE TOP 10
      // ----------------------------------------------------------------------

      const finalData:
        ChartDataItem[] =
        topTen.map(
          (
            item,
            index,
          ) => ({
            ...item,

            chartColor:
              getDistributedChartColor(
                index,
                totalPieItems,
              ),

            originalIndex:
              item.originalIndex,

            isOthers: false,
          }),
        );

      // ----------------------------------------------------------------------
      // ADD OTHERS TO PIE
      //
      // IMPORTANT:
      // Previously Others was only added to
      // the legend. Now it is also added to
      // chartData so it appears in the donut.
      // ----------------------------------------------------------------------

      if (
        remaining.length > 0 &&
        othersPercentage > 0
      ) {
        finalData.push({
          ...topTen[0],

          categoryName:
            'Others',

          subCategoryName:
            'Others',

          percentage:
            Number(
              othersPercentage.toFixed(
                2,
              ),
            ),

          chartColor:
            '#CBD5E1',

          originalIndex:
            -1,

          isOthers: true,
        });
      }

      return finalData;
    }, [data]);

  // ========================================================================
  // HAS DATA
  // ========================================================================

  const hasData =
    chartData.length > 0;

  // ========================================================================
  // HOVER
  // ========================================================================

  const [
    hoveredIndex,
    setHoveredIndex,
  ] = useState<
    number | null
  >(null);

  // ========================================================================
  // CHART REF
  // ========================================================================

  const chartWrapperRef =
    useRef<HTMLDivElement | null>(
      null,
    );

  // ========================================================================
  // CHART SIZE
  // ========================================================================

  const [
    chartSize,
    setChartSize,
  ] = useState({
    width: 0,
    height: 0,
  });

  // ========================================================================
  // RESPONSIVE SIZE
  // ========================================================================

  useEffect(() => {
    const element =
      chartWrapperRef.current;

    if (!element) {
      return;
    }

    const updateSize = () => {
      const rect =
        element.getBoundingClientRect();

      if (
        rect.width > 0 &&
        rect.height > 0
      ) {
        setChartSize({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateSize();

    const resizeObserver =
      new ResizeObserver(() => {
        updateSize();
      });

    resizeObserver.observe(
      element,
    );

    window.addEventListener(
      'resize',
      updateSize,
    );

    return () => {
      resizeObserver.disconnect();

      window.removeEventListener(
        'resize',
        updateSize,
      );
    };
  }, []);

  // ========================================================================
  // DONUT SIZE
  // ========================================================================

  const safeWidth =
    chartSize.width > 0
      ? chartSize.width
      : 320;

  const safeHeight =
    chartSize.height > 0
      ? chartSize.height
      : 260;

  const widthRadius =
    safeWidth * 0.24;

  const heightRadius =
    Math.max(
      safeHeight / 2 - 24,
      55,
    );

  const outerRadius =
    Math.min(
      widthRadius,
      heightRadius,
      105,
    );

  const innerRadius =
    Math.max(
      outerRadius * 0.58,
      35,
    );

  // ========================================================================
  // EMPTY STATE
  // ========================================================================

  if (!hasData) {
    return (
      <div
        className="
          box-border
          min-h-[360px]
          w-full
          min-w-0
          rounded-2xl
          border
          border-slate-200
          bg-white
          p-4
        "
      >
        {/* HEADER */}

        <div
          className="
            mb-3
            flex
            min-w-0
            flex-wrap
            items-center
            gap-2
          "
        >
          <h3
            className="
              shrink-0
              text-[16px]
              font-semibold
              text-[#003E7E]
            "
          >
            Idle Time Breakdown
          </h3>

          <span
            className="
              shrink-0
              rounded-full
              bg-blue-50
              px-3
              py-1
              text-[11px]
              font-medium
              text-blue-600
            "
          >
            Top 10 Subcategory + Others
          </span>
        </div>

        {/* EMPTY */}

        <div
          className="
            flex
            h-[250px]
            w-full
            items-center
            justify-center
            rounded-xl
            border
            border-dashed
            border-slate-300
          "
        >
          <span
            className="
              text-sm
              text-slate-500
            "
          >
            No Breakdown Data Available
          </span>
        </div>
      </div>
    );
  }

  // ========================================================================
  // MAIN CARD
  // ========================================================================

  return (
    <div
      className="
        box-border
        w-full
        min-w-0
        max-w-full
        rounded-2xl
        border
        border-slate-200
        bg-white
        p-4
      "
    >
      {/* ==================================================================
          HEADER
      ================================================================== */}

      <div
        className="
          mb-2
          flex
          w-full
          min-w-0
          flex-wrap
          items-center
          gap-2
        "
      >
        <h3
          className="
            shrink-0
            text-[16px]
            font-semibold
            text-[#003E7E]
          "
        >
          Idle Time Breakdown
        </h3>

        <span
          className="
            shrink-0
            rounded-full
            bg-blue-50
            px-3
            py-1
            text-[11px]
            font-medium
            text-blue-600
          "
        >
          Top 10 Subcategory + Others
        </span>
      </div>

      {/* ==================================================================
          CHART
      ================================================================== */}

      <div
        className="
          flex
          w-full
          min-w-0
          max-w-full
          flex-col
          items-center
        "
      >
        {/* ================================================================
            DONUT
            TOP 10 + OTHERS
        ================================================================ */}

        <div
          ref={chartWrapperRef}
          className="
            relative
            h-[260px]
            w-full
            min-w-0
            max-w-[760px]
            overflow-visible
          "
        >
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <PieChart
              margin={{
                top: 8,
                right: 8,
                bottom: 8,
                left: 8,
              }}
            >
              <Pie
                data={chartData}
                dataKey="percentage"
                nameKey="subCategoryName"
                cx="50%"
                cy="50%"
                innerRadius={
                  innerRadius
                }
                outerRadius={
                  outerRadius
                }
                paddingAngle={1}
                minAngle={2}
                labelLine={false}
                label={
                  renderCustomizedLabel
                }
                isAnimationActive={false}
                onMouseEnter={(
                  _,
                  index,
                ) => {
                  setHoveredIndex(
                    index,
                  );
                }}
                onMouseLeave={() => {
                  setHoveredIndex(
                    null,
                  );
                }}
              >
                {chartData.map(
                  (
                    item,
                    index,
                  ) => {
                    const isHovered =
                      hoveredIndex ===
                      index;

                    return (
                      <Cell
                        key={`
                          ${item.subCategoryName}
                          -
                          ${index}
                        `}
                        fill={
                          item.chartColor
                        }
                        stroke="#FFFFFF"
                        strokeWidth={1}
                        fillOpacity={
                          hoveredIndex ===
                            null ||
                          isHovered
                            ? 1
                            : 0.25
                        }
                      />
                    );
                  },
                )}
              </Pie>

              {/* ==========================================================
                  TOOLTIP
              ========================================================== */}

              <Tooltip
                content={
                  <CustomTooltip />
                }
                wrapperStyle={{
                  outline: 'none',
                  zIndex: 1000,
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* ================================================================
            TOP 10 + OTHERS LEGEND
        ================================================================ */}

        <div
          className="
            mt-2
            grid
            w-full
            min-w-0
            max-w-[900px]
            grid-cols-1
            gap-x-5
            gap-y-2
            px-1
            sm:grid-cols-2
            lg:grid-cols-3
          "
        >
          {chartData.map(
            (
              item,
              index,
            ) => {
              const isHovered =
                hoveredIndex ===
                index;

              return (
                <div
                  key={`
                    ${item.subCategoryName}
                    -
                    legend
                    -
                    ${index}
                  `}
                  className="
                    flex
                    min-w-0
                    cursor-pointer
                    items-center
                    gap-2
                    rounded-md
                    px-1
                    py-0.5
                    transition-all
                    duration-200
                  "
                  style={{
                    opacity:
                      hoveredIndex ===
                        null ||
                      isHovered
                        ? 1
                        : 0.38,
                  }}
                  onMouseEnter={() => {
                    setHoveredIndex(
                      index,
                    );
                  }}
                  onMouseLeave={() => {
                    setHoveredIndex(
                      null,
                    );
                  }}
                  title={`
                    ${item.subCategoryName}
                    (${Number(
                      item.percentage ??
                        0,
                    ).toFixed(1)}%)
                  `}
                >
                  {/* ======================================================
                      COLOR
                  ====================================================== */}

                  <span
                    className="
                      h-3
                      w-3
                      shrink-0
                      rounded-full
                      ring-2
                      ring-white
                      shadow-sm
                    "
                    style={{
                      backgroundColor:
                        item.chartColor,
                    }}
                  />

                  {/* ======================================================
                      NAME
                  ====================================================== */}

                  <span
                    className={`
                      min-w-0
                      flex-1
                      truncate
                      text-[11px]
                      ${
                        isHovered
                          ? 'font-semibold text-[#003E7E]'
                          : 'text-slate-600'
                      }
                    `}
                  >
                    {item.subCategoryName ||
                      'Unknown'}
                  </span>

                  {/* ======================================================
                      PERCENTAGE
                  ====================================================== */}

                  <span
                    className={`
                      shrink-0
                      text-[11px]
                      font-semibold
                      ${
                        item.isOthers
                          ? 'text-slate-500'
                          : isHovered
                            ? 'text-[#003E7E]'
                            : 'text-slate-500'
                      }
                    `}
                  >
                    {Number(
                      item.percentage ??
                        0,
                    ).toFixed(1)}
                    %
                  </span>
                </div>
              );
            },
          )}
        </div>
      </div>
    </div>
  );
};

export default IdleBreakdownChart;